import type { MarketingCapability, MeResponse, StaffRole } from '@ucp/web/lib';

/**
 * Marketing Requests roles per capability (PRO-3210, ADR-018 PRO-3210 amendment). Mirrors the
 * server map in `apps/api/src/auth/capabilities.ts` (asserted by
 * `apps/api/test/marketingAccessModel.test.ts`). UX only: the server re-checks every call.
 * `marketing_request_submit` is also carried by the workforce baseline pack (ADR-071 A2), so it
 * has no role here.
 */
export const MARKETING_CAPABILITY_ROLES: Readonly<
  Record<MarketingCapability, readonly StaffRole[]>
> = {
  marketing_request_submit: [],
  marketing_request_work: ['MARKETING_REQUEST_HANDLER', 'MARKETING_REQUEST_LEAD'],
  marketing_request_owner_config: ['MARKETING_REQUEST_LEAD'],
  marketing_request_provision_read: ['PRINCIPAL', 'SCHOOL_BUSINESS_MANAGER'],
  marketing_exec_comms: ['MARKETING_EXEC_COMMS_MEMBER'],
};

export const MARKETING_HANDLER_ROLES = MARKETING_CAPABILITY_ROLES.marketing_request_work;
export const MARKETING_LEAD_ROLES = MARKETING_CAPABILITY_ROLES.marketing_request_owner_config;
export const MARKETING_READER_ROLES = MARKETING_CAPABILITY_ROLES.marketing_request_provision_read;
export const MARKETING_EXEC_ROLES = MARKETING_CAPABILITY_ROLES.marketing_exec_comms;

/**
 * Roles the `/marketing/*` guard and the module chrome admit. Everyone else enters through the
 * baseline pack's `marketing_request_submit`.
 */
export const MARKETING_ROUTE_ROLES: readonly StaffRole[] = [
  ...new Set([
    ...MARKETING_HANDLER_ROLES,
    ...MARKETING_LEAD_ROLES,
    ...MARKETING_READER_ROLES,
    ...MARKETING_EXEC_ROLES,
  ]),
];

/** Does the caller hold one of these roles anywhere? */
export function marketingHoldsAny(
  user: Pick<MeResponse, 'roles'>,
  roles: readonly StaffRole[],
): boolean {
  return user.roles.some((r) => roles.includes(r.role));
}
